import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Icon, Tooltip } from '../../../shared/ui';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import {
  CreateTeamModal,
  teamsModel,
  useTeamPermissions,
} from '../../../features/teams';
import { IconMap } from '../../../shared/sprite';
import { useCurrentPlan } from '../../../features/subscription/hooks';
import {
  TEAM_SETTINGS_INFO_ROUTE,
  TEAM_SETTINGS_BRAND_ROUTE,
  TEAM_SETTINGS_MEMBERS_ROUTE,
  TEAM_SETTINGS_SUBSCRIPTION_ROUTE,
  TEAM_SETTINGS_CALL_RECORDINGS,
  TEAM_SETTINGS_STYLES_ROUTE,
  TEAM_SETTINGS_PERMISSIONS_CONTROL_ROUTE,
  redirectActions,
  TEAM_SETTINGS_INTEGRATIONS_ROUTE,
} from '../../../entities/history';

import { UserToTeamRole } from '@distribute/shared/types';
import { authUserModel } from '../../../entities/auth-user';
import { useWindowWidth } from '@distribute/frontend/utils';

const useTeamDropdownNavigation = () => {
  const dispatch = useDispatch();

  const { isGuest, isOwner, isCanManageSubscription, isCanManageAccess } =
    useTeamPermissions();

  return useMemo(() => {
    const navigationOptions = [
      {
        id: 'workspace-settings',
        name: 'Workspace settings',
        icon: IconMap.WorkspaceSettings,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_INFO_ROUTE,
            })
          );
        },
        isHide: isGuest,
      },
      {
        id: 'permissions-control',
        name: 'Permission control',
        icon: IconMap.Shield02,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_PERMISSIONS_CONTROL_ROUTE,
            })
          );
        },
        isHide: !isOwner || isGuest,
      },
      {
        id: 'team-brand',
        name: 'Company information',
        icon: IconMap.BookClosed,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_BRAND_ROUTE,
            })
          );
        },
        isHide: isGuest,
      },
      {
        id: 'call-recordings',
        name: 'Call recording settings',
        icon: IconMap.HeadPhones02,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_CALL_RECORDINGS,
            })
          );
        },
        isHide: isGuest,
      },
      {
        id: 'team-branding',
        name: 'Branding',
        icon: IconMap.Palette,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_STYLES_ROUTE,
            })
          );
        },
        isHide: isGuest,
      },
      {
        id: 'members',
        name: 'Members',
        icon: IconMap.Members,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_MEMBERS_ROUTE,
            })
          );
        },
        isHide: isGuest,
      },
      {
        id: 'plans-billing',
        name: 'Plans & billing',
        icon: IconMap.Award,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_SUBSCRIPTION_ROUTE,
            })
          );
        },
        isHide: !isCanManageSubscription || isGuest,
      },
      {
        id: 'team-integrations',
        name: 'Team integrations',
        icon: IconMap.PuzzlePiece01,
        onClick: () => {
          dispatch(
            redirectActions.fromWorkspaceWithBackUrl({
              path: TEAM_SETTINGS_INTEGRATIONS_ROUTE,
            })
          );
        },
        isHide: !isCanManageAccess || isGuest,
      },
    ].filter((i) => !i.isHide);

    return navigationOptions;
  }, [isGuest, isOwner, isCanManageSubscription, dispatch, isCanManageAccess]);
};

const useTeamDropdownTeams = () => {
  const dispatch = useDispatch();
  const teams = useSelector(teamsModel.selectors.selectTeams);

  return useMemo(() => {
    return teams.map((team) => {
      return {
        id: team.id.toString(),
        label: team.name,
        logoUrl: team.brandLogoUrl,
        onClick: () =>
          dispatch(teamsModel.actions.changeCurrentTeam({ teamId: team.id })),
      };
    });
  }, [teams, dispatch]);
};

export const SelectTeamDropdown: FC = () => {
  const dispatch = useDispatch();

  const currentTeam = useSelector(
    teamsModel.selectors.selectCurrentTeamWithError
  );
  const currentUser = useSelector(authUserModel.selectors.selectUserWithError);
  const currentPlan = useCurrentPlan();

  const { isMobile } = useWindowWidth();

  const { isGuest } = useTeamPermissions();

  const navigationOptions = useTeamDropdownNavigation();
  const teams = useTeamDropdownTeams();

  const isShowCreateNewTeamModal = useSelector(
    teamsModel.selectors.selectIsShowCreateNewTeamModal
  );

  const setCreateNewTeamModalOpen = (isOpen: boolean) => {
    if (currentUser.isOwnTeamWasCreated) {
      dispatch(teamsModel.actions.setIsShowCreateNewTeamModal(isOpen));
    } else {
      dispatch(teamsModel.actions.createTeamForGuest());
    }
  };

  const membersCount = currentTeam.userToTeams.filter(
    (member) =>
      member.role !== UserToTeamRole.GUEST &&
      member.user &&
      !member.isWaitingForInviteAcceptance
  ).length;

  const tooltipText = `${currentPlan.name} plan • ${membersCount} ${
    membersCount > 1 ? 'members' : 'member'
  }`;

  return (
    <div className="flex items-center justify-center">
      <CreateTeamModal
        isOpen={isShowCreateNewTeamModal}
        onClose={() => setCreateNewTeamModalOpen(false)}
      />
      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="outline-none">
          <Tooltip
            className="font-semibold px-3 py-2"
            sideOffset={4}
            align="start"
            hideArrow
            isOpen={!isMobile && !isGuest && undefined}
            trigger={
              <button
                type="button"
                className="py-1 pl-1 pr-2 flex items-center gap-2 md:w-auto w-[248px] h-10 rounded-md text-md font-semibold text-gray-700 bg-transparent hover:bg-base-black/[0.04] focus:outline-none"
              >
                <Avatar
                  displayName={currentTeam.name}
                  src={currentTeam.brandLogoUrl as string}
                  theme={
                    currentTeam.brandLogoUrl ? 'primary-rect' : 'secondary-rect'
                  }
                  className="w-8 h-8 rounded-md"
                />
                <p className="truncate md:hidden">{currentTeam.name}</p>
                <Icon
                  className="flex-shrink-0 ml-auto text-gray-600"
                  glyph={IconMap.ChevronSelectorVerticalSmall}
                  width={20}
                />
              </button>
            }
          >
            {tooltipText}
          </Tooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          sideOffset={4}
          align="start"
          className="flex flex-col gap-4 border rounded-xl bg-base-white border-gray-200 focus:outline-none overflow-hidden pt-4 pb-2 px-2 z-10 shadow-lg w-70"
        >
          {!isGuest && (
            <>
              <div className="flex flex-col md:hidden">
                <p className="text-xs font-bold text-gray-500 mb-2 uppercase truncate px-3">
                  {currentTeam.name}
                </p>
                {navigationOptions.map((item) => (
                  <DropdownMenu.Item
                    key={item.id}
                    onSelect={item.onClick}
                    className="group outline-none px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 flex flex-nowrap items-center gap-3 hover:bg-base-black/[0.04] focus:bg-base-black/[0.04] rounded-md cursor-pointer"
                  >
                    <Icon
                      glyph={item.icon}
                      width={20}
                      className="text-gray-500 group-hover:text-gray-600"
                    />
                    <p className="truncate">{item.name}</p>
                  </DropdownMenu.Item>
                ))}
              </div>
              <div className="md:hidden -mx-2 h-0.25 bg-gray-200" />
            </>
          )}
          <div className="flex flex-col">
            <p className="text-xs font-bold text-gray-500 mb-2 uppercase px-3">
              Teams
            </p>
            {teams.map((item) => (
              <DropdownMenu.Item
                key={item.id}
                onSelect={item.onClick}
                className="group outline-none px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 flex flex-nowrap items-center gap-2 hover:bg-base-black/[0.04] focus:bg-base-black/[0.04] rounded-md cursor-pointer"
              >
                <Avatar
                  displayName={item.label}
                  src={item.logoUrl as string}
                  theme={item.logoUrl ? 'primary-rect' : 'secondary-rect'}
                  size="xs"
                />
                <p className="truncate">{item.label}</p>
                {item.id === `${currentTeam.id}` && (
                  <Icon
                    glyph={IconMap.GreenCheckMark}
                    width={20}
                    className="ml-auto flex-shrink-0"
                  />
                )}
              </DropdownMenu.Item>
            ))}
            <DropdownMenu.Item
              onSelect={() => setCreateNewTeamModalOpen(true)}
              className="md:hidden group outline-none px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 flex flex-nowrap items-center gap-3 hover:bg-base-black/[0.04] focus:bg-base-black/[0.04] rounded-md cursor-pointer"
            >
              <Icon
                glyph={IconMap.PlusLight}
                width={20}
                className="text-gray-500 group-hover:text-gray-600"
              />
              <p className="truncate">Add Team</p>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
};
